// /disputes/alerts — admin alert settings + delivery history (SM-293).
//
// Two things an operator needs from this page:
//
//   1. "Will we actually be told when a dispute lands?" — the route
//      matrix, with the recipient rule stated plainly: dispute email
//      goes to whoever holds DISPUTE_REVIEW, so the way to change the
//      recipients is to change the role grants, not a list here.
//   2. "Did the last one arrive?" — the delivery history, including
//      failures. A DEAD row is the visible form of "an administrator was
//      never told about a dispute holding up everyone's payout", which
//      is why failures are rendered with their provider error rather
//      than hidden behind a status word.
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";

import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { EmptyState } from "@/components/ui/EmptyState";
import { ErrorPanel } from "@/components/ui/ErrorPanel";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Skeleton } from "@/components/ui/Skeleton";
import { useMyPerms } from "@/hooks/useMyPerms";
import {
  useAlertDeliveries,
  useAlertHealth,
  useAlertRoutes,
  useSendTestAlert,
  useUpdateAlertRoute,
  type AlertDeliveryItem,
  type AlertRouteItem,
} from "@/lib/dispute-queries";
import { humanizeError } from "@/lib/errorMessages";
import { fmtRelative } from "@/lib/format";
import { Perm } from "@/lib/perms";
import { toast } from "@/lib/toast";

export const Route = createFileRoute("/_app/disputes/alerts")({
  component: DisputeAlertsPage,
});

// Human label per alert event. Falls back to the raw token so a newly
// added event type is still labelled (just not prettified) rather than
// rendering an unheaded group.
const ALERT_EVENT_LABELS: Record<string, string> = {
  DISPUTE_OPENED: "When a dispute is filed",
  DISPUTE_OVERDUE: "When a dispute goes overdue",
};

function alertEventLabel(eventType: string): string {
  return ALERT_EVENT_LABELS[eventType] ?? eventType;
}

// Collapse the flat (event × channel) route matrix into one group per
// event, preserving the API's order (event_type, channel). See SM-316:
// without the grouping the repeated channel names read as duplicates.
function groupRoutesByEvent(
  items: AlertRouteItem[],
): { eventType: string; items: AlertRouteItem[] }[] {
  const groups = new Map<string, AlertRouteItem[]>();
  for (const r of items) {
    const g = groups.get(r.event_type);
    if (g) g.push(r);
    else groups.set(r.event_type, [r]);
  }
  return Array.from(groups, ([eventType, rows]) => ({ eventType, items: rows }));
}

function DisputeAlertsPage() {
  const perms = useMyPerms();
  const routes = useAlertRoutes();
  const update = useUpdateAlertRoute();
  const [statusFilter, setStatusFilter] = useState("");
  const deliveries = useAlertDeliveries({
    status: statusFilter || undefined,
    limit: 50,
  });

  if (!perms.has(Perm.AlertView)) {
    return <EmptyState title="No access" description="You need ALERT_VIEW." />;
  }
  const canConfigure = perms.has(Perm.AlertConfigure);

  return (
    <div className="space-y-6">
      <div>
        <Link to="/disputes" className="text-2xs text-muted hover:text-fg">
          ← Disputes
        </Link>
        <h1 className="mt-1 font-display text-2xl tracking-tight">Dispute alerts</h1>
        <p className="text-sm text-muted">
          Who gets told when a dispute is filed or goes overdue. Email reaches every
          active administrator holding{" "}
          <span className="text-fg">DISPUTE_REVIEW</span> — grant or revoke that role
          permission to change the recipients.
        </p>
      </div>

      <AlertHealthPanel />

      <Card>
        <CardHeader className="border-b border-border">
          <CardTitle>Channels</CardTitle>
        </CardHeader>
        <CardBody className="p-0">
          {routes.isLoading && (
            <div className="space-y-2 p-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-10 rounded-lg" />
              ))}
            </div>
          )}
          {routes.isError && (
            <ErrorPanel error={routes.error} reset={() => void routes.refetch()} />
          )}
          {routes.data &&
            // The route matrix is (event × channel): the same five channels
            // exist for every dispute event, so a flat list repeats each
            // channel name once per event and reads as duplicates (SM-316).
            // Group under one header per event to make each row's event
            // explicit instead of burying it in a subtitle.
            groupRoutesByEvent(routes.data.items).map((group) => (
              <section key={group.eventType} className="border-b border-border last:border-b-0">
                <div className="flex items-baseline gap-2 border-b border-border bg-bg-3 px-4 py-2">
                  <h3 className="text-sm font-medium text-fg">
                    {alertEventLabel(group.eventType)}
                  </h3>
                  <span className="font-mono text-2xs text-muted">{group.eventType}</span>
                </div>
                <ul className="divide-y divide-border">
                  {group.items.map((r) => (
                    <li
                      key={r.route_id}
                      className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-4 py-3"
                    >
                      <p className="min-w-0 flex-1 text-sm text-fg">
                        {r.channel}
                        {!r.available && (
                          <span className="ml-2 text-2xs text-muted">no adapter in this build</span>
                        )}
                      </p>
                      <Badge tone={r.enabled ? "success" : "muted"}>
                        {r.enabled ? "On" : "Off"}
                      </Badge>
                      <Button
                        size="sm"
                        variant="secondary"
                        // A channel with no adapter cannot be turned on — the
                        // server refuses, and offering the control would let
                        // an operator believe alerts were going out.
                        disabled={!canConfigure || (!r.enabled && !r.available)}
                        loading={update.isPending}
                        onClick={() =>
                          update.mutate({
                            eventType: r.event_type,
                            channel: r.channel,
                            enabled: !r.enabled,
                          })
                        }
                      >
                        {r.enabled ? "Disable" : "Enable"}
                      </Button>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          {update.error && (
            <p className="px-4 py-2 text-sm text-danger">{humanizeError(update.error)}</p>
          )}
        </CardBody>
      </Card>

      {canConfigure && <TestSendCard />}

      <Card>
        <CardHeader className="gap-3 border-b border-border">
          <CardTitle>Delivery history</CardTitle>
          <Select
            className="ml-auto w-40"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            placeholder="All statuses"
            options={[
              { value: "SENT", label: "Sent" },
              { value: "PENDING", label: "Pending" },
              { value: "FAILED", label: "Failed" },
              { value: "DEAD", label: "Dead" },
            ]}
          />
        </CardHeader>
        <CardBody className="p-0">
          {deliveries.isLoading && (
            <div className="space-y-2 p-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12 rounded-lg" />
              ))}
            </div>
          )}
          {deliveries.isError && (
            <ErrorPanel error={deliveries.error} reset={() => void deliveries.refetch()} />
          )}
          {deliveries.data?.items.length === 0 && (
            <EmptyState title="No deliveries" description="No alerts have been sent yet." />
          )}
          {deliveries.data && deliveries.data.items.length > 0 && (
            <ul className="divide-y divide-border">
              {deliveries.data.items.map((d) => (
                <DeliveryRow key={d.delivery_id} d={d} />
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

/**
 * Configuration health — the answer to "would an alert raised right now
 * actually arrive?".
 *
 * This panel exists because every way this subsystem fails looks
 * identical from the page below it: the route matrix reads "On", the
 * history is empty or full of terse provider codes, and nothing says the
 * sender domain was never verified. SM-293 sat in that state for eight
 * days. So the check is stated up front, in the operator's terms, with
 * the fix in the text rather than in a runbook.
 */
function AlertHealthPanel() {
  const health = useAlertHealth();

  if (health.isLoading) return <Skeleton className="h-24 rounded-xl" />;
  // A failed health check is not worth an error panel above the page's
  // actual content — the rest of the console still works, and the routes
  // card below carries its own error state.
  if (health.isError || !health.data) return null;

  const { status, channels, coverage, problems } = health.data;
  const ok = status === "OK";
  const tone = status === "MISCONFIGURED" ? "danger" : status === "DEGRADED" ? "warning" : "success";

  return (
    <Card className={ok ? undefined : "border-danger/40"}>
      <CardHeader className="gap-3 border-b border-border">
        <CardTitle>Delivery readiness</CardTitle>
        <Badge tone={tone} className="ml-auto">
          {ok ? "Ready" : status === "DEGRADED" ? "Degraded" : "Not delivering"}
        </Badge>
        <Button
          size="sm"
          variant="secondary"
          loading={health.isFetching}
          onClick={() => void health.refetch()}
        >
          Re-check
        </Button>
      </CardHeader>
      <CardBody className="space-y-3">
        {problems && problems.length > 0 && (
          <ul className="space-y-1.5">
            {problems.map((p) => (
              <li
                key={p}
                className="break-words rounded bg-danger/10 px-2.5 py-1.5 text-2xs text-danger"
              >
                {p}
              </li>
            ))}
          </ul>
        )}
        {channels
          .filter((c) => c.routed)
          .map((c) => (
            <div key={c.channel} className="text-2xs text-muted">
              <span className="text-fg">{c.channel}</span>
              {c.sender && <> · sending as <span className="font-mono">{c.sender}</span></>}
              {" · "}
              {c.recipient_count} recipient{c.recipient_count === 1 ? "" : "s"}
              {c.unreachable_recipients && c.unreachable_recipients.length > 0 && (
                <>
                  {" · "}
                  <span className="text-warning">
                    {c.unreachable_recipients.length} undeliverable:{" "}
                    {c.unreachable_recipients.join(", ")}
                  </span>
                </>
              )}
              {ok && c.detail && <> · {c.detail}</>}
            </div>
          ))}
        <div className="text-2xs text-muted">
          {coverage.map((c) => (
            <span key={c.event_type} className="mr-3 inline-block">
              <span className="font-mono">{c.event_type}</span>{" "}
              {c.routed ? (
                <span className="text-fg">→ {c.enabled_channels?.join(", ")}</span>
              ) : (
                <span className="text-danger">→ nobody</span>
              )}
            </span>
          ))}
        </div>
      </CardBody>
    </Card>
  );
}

function DeliveryRow({ d }: { d: AlertDeliveryItem }) {
  const tone =
    d.status === "SENT"
      ? "success"
      : d.status === "DEAD"
        ? "danger"
        : d.status === "FAILED"
          ? "warning"
          : "muted";
  return (
    <li className="px-4 py-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <Badge tone={tone}>{d.status}</Badge>
        <span className="text-sm text-fg">{d.recipient}</span>
        <span className="text-2xs text-muted">{d.event_type} · {d.channel}</span>
        <span className="ml-auto text-2xs text-muted">
          {fmtRelative(new Date(d.created_at_ms).toISOString())}
          {d.attempts > 1 && ` · ${d.attempts} attempts`}
        </span>
      </div>
      {d.subject && <p className="mt-1 truncate text-2xs text-muted">{d.subject}</p>}
      {/* The provider's own error, verbatim. "Failed" alone tells an
          operator nothing they can act on. */}
      {d.last_error && (
        <p className="mt-1 break-words rounded bg-danger/10 px-2 py-1 text-2xs text-danger">
          {d.last_error}
        </p>
      )}
      {d.dispute_id && (
        <Link
          to="/disputes/$id"
          params={{ id: d.dispute_id }}
          className="mt-1 inline-block text-2xs text-primary hover:underline"
        >
          View dispute
        </Link>
      )}
    </li>
  );
}

function TestSendCard() {
  const send = useSendTestAlert();
  const [recipient, setRecipient] = useState("");

  async function run() {
    try {
      const res = await send.mutateAsync({ channel: "EMAIL", recipient: recipient.trim() || undefined });
      // The send is attempted inline, so the response carries the real
      // outcome — reporting "queued" would prove nothing about whether
      // the provider is reachable.
      const ok = res.delivery.status === "SENT";
      if (ok) toast.success(`Test alert delivered to ${res.delivery.recipient}`);
      else toast.error(res.delivery.last_error || "Test alert failed");
    } catch {
      // Surfaced inline.
    }
  }

  return (
    <Card>
      <CardHeader className="border-b border-border">
        <CardTitle>Send a test</CardTitle>
      </CardHeader>
      <CardBody className="flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="mb-1 block text-2xs uppercase tracking-wide text-muted">
            Recipient (blank = you)
          </span>
          <Input
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            // Not an example.com address: that domain is reserved by RFC
            // 2606 and the server now rejects it outright, so suggesting
            // it would hand the operator a guaranteed failure.
            placeholder="ops@devcula.com"
            className="w-64"
          />
        </label>
        <Button onClick={() => void run()} loading={send.isPending}>
          Send test email
        </Button>
        {send.error && <p className="text-sm text-danger">{humanizeError(send.error)}</p>}
      </CardBody>
    </Card>
  );
}
